import { GlobeAltIcon, UserIcon } from '@heroicons/react/24/outline';
import { ComponentPropsWithoutRef, ComponentType } from 'react';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { restUrl } from '@/services/utils';
import { DataModelAttribute } from '@/types/DataModel';

type AccountProfileVariant = 'organization' | 'user';

interface AttributeRootScope {
    prepareAttributes: (attributes: unknown[]) => void;
    dataModel: {
        getOrganizationAttributes: () => unknown[];
        getUserAttributes: () => unknown[];
    };
}

interface AccountImageConfig {
    uploadUrl: string;
    fileName: string;
    placeholderIcon: ComponentType<ComponentPropsWithoutRef<'svg'>>;
    idPrefix: string;
    className: string;
}

const IMAGE_BY_VARIANT: Record<AccountProfileVariant, Omit<AccountImageConfig, 'uploadUrl'> & { uploadPath: string }> = {
    organization: {
        fileName: 'organization.png',
        placeholderIcon: GlobeAltIcon,
        idPrefix: 'organization',
        className: 'organization-image-field',
        uploadPath: 'organization/image',
    },
    user: {
        fileName: 'user.png',
        placeholderIcon: UserIcon,
        idPrefix: 'user',
        className: '',
        uploadPath: 'user/image',
    },
};

function loadAccountAttributes(variant: AccountProfileVariant): DataModelAttribute[] {
    const rootScope = getRootScope(getInjector()) as AttributeRootScope;
    const list = (variant === 'organization'
        ? rootScope.dataModel.getOrganizationAttributes()
        : rootScope.dataModel.getUserAttributes()) || [];
    rootScope.prepareAttributes(list);
    return list as DataModelAttribute[];
}

function accountAttributeLabelId(variant: AccountProfileVariant, name: string) {
    return variant === 'user' ? `${name}_user_label` : `${name}_label`;
}

function accountImageConfig(variant: AccountProfileVariant): AccountImageConfig {
    const { uploadPath, ...config } = IMAGE_BY_VARIANT[variant];
    return {
        ...config,
        uploadUrl: restUrl(uploadPath),
    };
}

export {
    accountAttributeLabelId,
    accountImageConfig,
    loadAccountAttributes,
};
export type { AccountProfileVariant };
